// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Waft — the PURE camera derivation (principle #23): two consecutive grayscale frames →
// a per-zone motion energy in [0,1]. This is the single function every unit test drives,
// so the sensor path is proven WITHOUT a real camera. The live camera source (input.ts)
// does nothing but downsample the mirrored video to a grayscale buffer and hand it here;
// the game never sees a pixel, and no frame is ever recorded, stored, or sent.
//
// Zones tile the frame row-major (zone = row*cols + col), matching layout.ts, so a wave
// on the player's right (the display is mirrored in the source) lights the right zone.

/** Integer boundary of column/row `i` of `n` across `total` px — tiles exactly. */
function bound(i: number, n: number, total: number): number {
  return Math.round((i * total) / n);
}

/**
 * Mean absolute per-pixel difference in each zone, normalised to [0,1].
 * `prev`/`cur` are grayscale (0..255) buffers of length gridW*gridH.
 */
export function frameDiffZones(
  prev: ArrayLike<number>,
  cur: ArrayLike<number>,
  gridW: number,
  gridH: number,
  cols: number,
  rows: number,
): number[] {
  const out = new Array<number>(cols * rows).fill(0);
  for (let row = 0; row < rows; row++) {
    const y0 = bound(row, rows, gridH);
    const y1 = bound(row + 1, rows, gridH);
    for (let col = 0; col < cols; col++) {
      const x0 = bound(col, cols, gridW);
      const x1 = bound(col + 1, cols, gridW);
      let sum = 0;
      let count = 0;
      for (let y = y0; y < y1; y++) {
        const rowOff = y * gridW;
        for (let x = x0; x < x1; x++) {
          const idx = rowOff + x;
          const d = cur[idx] - prev[idx];
          sum += d < 0 ? -d : d;
          count++;
        }
      }
      out[row * cols + col] = count > 0 ? sum / (count * 255) : 0;
    }
  }
  return out;
}

/**
 * Blend a new reading into a running per-zone energy with a fast attack / slow release,
 * so a wave registers instantly but the pulse lingers a beat for the visual feedback.
 * Pure: returns a new array.
 */
export function smoothEnergy(prev: readonly number[], next: readonly number[], attack = 0.7, release = 0.25): number[] {
  const out = new Array<number>(next.length);
  for (let i = 0; i < next.length; i++) {
    const p = prev[i] ?? 0;
    const n = next[i] ?? 0;
    out[i] = n > p ? p + (n - p) * attack : p + (n - p) * release;
  }
  return out;
}
